import { useSearchParams } from '@remix-run/react';
import { useState } from 'react';

import type { ModalType } from '~/interfaces/modal_type';

// Types RelativeRoutingType and NavigationOptions are copied over from
// react-router package
type RelativeRoutingType = 'route' | 'path';

type NavigationOptions = {
  replace?: boolean;
  state?: any;
  preventScrollReset?: boolean;
  relative?: RelativeRoutingType;
  flushSync?: boolean;
  viewTransition?: boolean;
};

interface ReturnProps {
  currentModal: ModalType | null;
  isModalOpen: boolean;
  handleCloseModal: (options?: NavigationOptions, additionalParams?: string[]) => void;
  handleOpenModal: (modal: ModalType, options?: NavigationOptions, additionalParams?: Record<string, string>) => void;
}

const VALID_MODAL_TYPES: ModalType[] = [
  'search',
  'login',
  'signup',
  'recommendations',
  'edit_profile',
  'reset_password',
  'unsubscribe',
  'resubscribe',
  'change_plan',
  'unpause_plan',
  'downgrade',
  'unpause',
];

const useGlobalModals = ({ useLocalState }: { useLocalState: boolean } = { useLocalState: false }): ReturnProps => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localModalType, setLocalModalParam] = useState<ModalType | null>(null);

  const handleCloseModal = (options?: NavigationOptions, additionalParams?: string[]) => {
    if (useLocalState) {
      setLocalModalParam(null);
      return;
    }

    searchParams.delete('modal');
    additionalParams?.forEach((param) => searchParams.delete(param));
    setSearchParams(searchParams, { replace: true, ...options });
  };

  const handleOpenModal = (
    modal: ModalType,
    options?: NavigationOptions,
    additionalParams?: Record<string, string>,
  ) => {
    if (useLocalState) {
      setLocalModalParam(modal);
      return;
    }

    searchParams.set('modal', modal);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
    }

    setSearchParams(searchParams, { replace: true, ...options });
  };

  const currentModal = useLocalState ? localModalType : (searchParams.get('modal') as ModalType);

  return {
    currentModal,
    isModalOpen: currentModal ? VALID_MODAL_TYPES.includes(currentModal) : false,
    handleCloseModal,
    handleOpenModal,
  };
};

export default useGlobalModals;
